import React from "react";
import { Button, Badge, Card, Container, Row, Col } from "react-bootstrap";
import './TableLayout.css';
import AddButton from "components/ui/AddButton";
import Pagination from "components/ui/Pagination";

const layoutWrapper = (WrappedComponent) => {
    return function TableLayoutHOC({
        title = "",
        data = [],
        columns = [],
        actions = null,
        loading = false,
        emptyMessage = "No data available",
        filters = null,
        showFilters = false,
        onToggleFilters = null,
        filterConfig = [],
        onFilterChange = null,
        onClearFilters = null,
        customFilters = null,
        customHeader = null,
        pagination = null,
        onAddNew = null,
        addButtonText = "Add New ",
        gradient = "primary",
        icon = "fas fa-table",
        ...restProps
    }) {
        return (
            <Container fluid className="py-4">
                <Row>
                    <Col md="12">
                        <Card className="shadow border-0 rounded-3 overflow-hidden">
                            {/* Header with Gradient */}
                            <div
                                className="text-white py-3 px-3"
                                style={{
                                    background: 'linear-gradient(135deg, #149f9c 0%, #1ab3af 100%)'
                                }}
                            >
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h4 className="mb-0" style={{ fontSize: 'clamp(1rem, 4vw, 1.5rem)' }}>
                                            <i className={`${icon} me-2`}></i>
                                            <span className="d-none d-sm-inline">{title}</span>
                                            <span className="d-inline d-sm-none">{title.split(' ')[0]}</span>
                                        </h4>
                                    </div>
                                    <div className="d-flex gap-2 flex-wrap">
                                        {filters && (
                                            <Button
                                                variant="light"
                                                size="sm"
                                                onClick={onToggleFilters}
                                                className="d-flex align-items-center"
                                            >
                                                <i className="fas fa-filter me-1"></i>
                                                <span className="d-none d-md-inline">Filters</span>
                                                {Object.values(filters).filter(v => v).length > 0 && (
                                                    <Badge bg="danger" className="ms-2">
                                                        {Object.values(filters).filter(v => v).length}
                                                    </Badge>
                                                )}
                                            </Button>
                                        )}

                                        {onAddNew && (
                                            <AddButton
                                                onClick={onAddNew}
                                                icon="fas fa-plus-circle"
                                                variant="light"
                                            >
                                                <span className="d-none d-md-inline">{addButtonText}</span>
                                                <span className="d-inline d-md-none">Add</span>
                                            </AddButton>
                                            //   <Button 
                                            //     variant="light" 
                                            //     onClick={onAddNew}
                                            //     className="d-flex align-items-center"
                                            //   >
                                            //     <i className="fas fa-plus-circle me-1"></i>
                                            //     <span className="d-none d-md-inline">{addButtonText}</span>
                                            //     <span className="d-inline d-md-none">Add</span>
                                            //   </Button>
                                        )}

                                        {actions && <div className="d-flex gap-2">{actions}</div>}
                                    </div>
                                </div>
                            </div>

                            {/* Filters Section */}
                            {customFilters || (showFilters && filterConfig && onFilterChange && (
                                <div className="px-3 pt-3 border-bottom">
                                    <div className="row g-3">
                                        {filterConfig.map((field) => (
                                            <div key={field.key} className="col-md-4">
                                                <label className="form-label small text-muted mb-1">
                                                    {field.label}
                                                </label>
                                                <input
                                                    type={field.type || 'text'}
                                                    className="form-control form-control-custom"
                                                    placeholder={field.placeholder || `Search ${field.label.toLowerCase()}...`}
                                                    value={filters[field.key] || ''}
                                                    onChange={(e) => onFilterChange(field.key, e.target.value)}
                                                />
                                            </div>
                                        ))}
                                        {onClearFilters && (
                                            <div className="col-12">
                                                <div className="d-flex justify-content-end">
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        onClick={onClearFilters}
                                                    >
                                                        <i className="fas fa-times me-1"></i>
                                                        Clear Filters
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}

                            {/* Custom Header (Statistics) */}
                            {customHeader}

                            {/* Table Container */}
                            <Card.Body className="p-0">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-3 text-muted">Loading data...</p>
                                    </div>
                                ) : data.length === 0 ? (
                                    <div className="text-center py-5">
                                        <div className="display-1 text-muted mb-3">
                                            <i className="fas fa-inbox"></i>
                                        </div>
                                        <h4 className="text-muted mb-3">{emptyMessage}</h4>
                                        {onAddNew && (
                                            <Button
                                                variant="primary"
                                                onClick={onAddNew}
                                                className="px-4"
                                            >
                                                <i className="fas fa-plus me-2"></i>
                                                Create First Item
                                            </Button>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                    <div className="table-responsive">
                                        <table className="table table-hover mb-0">
                                            <thead className="bg-light">
                                                <tr>
                                                    {columns.map((col, idx) => (
                                                        <th
                                                            key={idx}
                                                            className={`border-0 ${idx === 0 ? 'ps-4' : ''} ${idx === columns.length - 1 ? 'pe-4 text-end' : ''}`}
                                                            style={{
                                                                backgroundColor: '#f8f9fa',
                                                                borderBottom: '2px solid #e9ecef',
                                                                padding: '1rem',
                                                                fontWeight: '600',
                                                                color: '#495057'
                                                            }}
                                                        >
                                                            {col.label}
                                                        </th>
                                                    ))}

                                                    {/* Actions Column Header */}
                                                    {restProps.showActions && (
                                                        <th
                                                            className="border-0 ps-4 pe-4 text-end"
                                                            style={{
                                                                backgroundColor: '#f8f9fa',
                                                                borderBottom: '2px solid #e9ecef',
                                                                padding: '1rem',
                                                                fontWeight: '600',
                                                                color: '#495057'
                                                            }}
                                                        >
                                                            Actions
                                                        </th>
                                                    )}
                                                </tr>
                                            </thead>

                                            <tbody>
                                                <WrappedComponent data={data} columns={columns} {...restProps} />
                                            </tbody>
                                        </table>
                                    </div>
                                    </>
                                )}
                            </Card.Body>

                            {/* Pagination Footer */}
                            {pagination && data.length > 0 && (
                                <Card.Footer className="bg-light border-0 py-3">
                                    <Pagination
                                        currentPage={pagination.currentPage}
                                        totalPages={pagination.totalPages}
                                        totalItems={pagination.totalItems}
                                        pageSize={pagination.pageSize}
                                        onPageChange={pagination.onPageChange}
                                        onNextPage={pagination.onNextPage}
                                        onPrevPage={pagination.onPrevPage}
                                        onPageSizeChange={pagination.onPageSizeChange}
                                    />
                                </Card.Footer>
                            )}
                        </Card>
                    </Col>
                </Row>
            </Container>
        );
    };
};

export default layoutWrapper;